/** Settings → Audit log (C11, P4-07): who did what and when, read by the owner and the manager. */

import { ApiError, type AuditLog, useAuditLogList } from "@agencyos/api-client";
import { GlassCard, Skeleton } from "@agencyos/ui-web";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { SettingsLayout } from "../components/SettingsLayout";
import { dateTimeText, Pager } from "./billing/shared";

const AREAS = ["all", "billing", "contracts", "catalog", "team", "immigration"] as const;
type Area = (typeof AREAS)[number];

/** One JSON value, short and readable (the changes come from the API as free JSON). */
function valueText(value: unknown, empty: string): string {
  if (value === null || value === undefined || value === "") return empty;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    const text = JSON.stringify(value);
    return text.length > 80 ? `${text.slice(0, 77)}…` : text;
  } catch {
    return empty;
  }
}

/** `{ field: [before, after] }` → "field: before → after"; other values → "field: value". */
export function changesText(changes: unknown, empty = "—"): string[] {
  if (changes === null || typeof changes !== "object") return changes == null || changes === "" ? [] : [valueText(changes, empty)];
  if (Array.isArray(changes)) return changes.length ? [valueText(changes, empty)] : [];
  return Object.entries(changes as Record<string, unknown>).map(([key, value]) =>
    Array.isArray(value) && value.length === 2
      ? `${key}: ${valueText(value[0], empty)} → ${valueText(value[1], empty)}`
      : `${key}: ${valueText(value, empty)}`,
  );
}

function Rows({ rows }: { rows: AuditLog[] }) {
  const { t, i18n } = useTranslation();
  const action = (code: string) => (i18n.exists(`audit.actions.${code}`) ? t(`audit.actions.${code}`) : code);
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">{t("audit.date")}</th>
            <th scope="col">{t("audit.who")}</th>
            <th scope="col">{t("audit.action")}</th>
            <th scope="col">{t("audit.object")}</th>
            <th scope="col">{t("audit.changes")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const lines = changesText(row.changes);
            return (
              <tr key={row.id}>
                <td>{dateTimeText(row.created_at, i18n.language)}</td>
                <td>{row.actor_email || <span className="muted">{t("audit.system")}</span>}</td>
                <td>{action(row.action)}</td>
                <td className="muted">{row.object_type || "—"}</td>
                <td>
                  {lines.length ? (
                    <ul className="plain-list">
                      {lines.map((line, index) => (
                        <li key={index}>
                          <small>{line}</small>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function AuditPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [area, setArea] = useState<Area>("all");
  const log = useAuditLogList({ page });
  const forbidden = log.error instanceof ApiError && log.error.status === 403;
  const data = log.data?.status === 200 ? log.data.data : undefined;
  const rows = (data?.results ?? []).filter((row) => area === "all" || row.action.startsWith(`${area}.`));

  let content;
  if (forbidden) content = <p role="alert">{t("audit.forbidden")}</p>;
  else if (log.isPending) content = <Skeleton style={{ height: 200 }} />;
  else if (!data) content = <p role="alert">{t("common.error")}</p>;
  else if (!data.results.length) content = <p className="muted">{t("audit.empty")}</p>;
  else
    content = (
      <>
        <div className="tabs" role="group" aria-label={t("audit.filterLabel")}>
          {AREAS.map((key) => (
            <button
              key={key}
              type="button"
              className={`tabs__tab${area === key ? " active" : ""}`}
              aria-pressed={area === key}
              onClick={() => setArea(key)}
            >
              {t(`audit.areas.${key}`)}
            </button>
          ))}
        </div>
        {rows.length ? <Rows rows={rows} /> : <p className="muted">{t("audit.emptyFilter")}</p>}
        <Pager
          page={page}
          hasNext={Boolean(data.next)}
          onChange={setPage}
          labels={{ previous: t("audit.previous"), next: t("audit.next"), page: t("audit.page", { page }) }}
        />
      </>
    );

  return (
    <SettingsLayout title={t("audit.title")} intro={t("audit.intro")}>
      <GlassCard as="section" className="stack">
        {content}
      </GlassCard>
    </SettingsLayout>
  );
}
